// src/pages/finance/SaldoUserPage.js
import React, { useEffect, useState } from 'react'
import { 
  collection, 
  collectionGroup, 
  query, 
  where, 
  getDocs, 
  doc, 
  runTransaction, 
  orderBy 
} from 'firebase/firestore'
import { db } from '../../firebaseConfig' 

// Helper format rupiah
const formatRupiah = (number) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(number)
}

export default function SaldoUserPage() {
  const [pendingTopups, setPendingTopups] = useState([])
  const [pendingWithdraws, setPendingWithdraws] = useState([]) // [BARU] State Withdraw
  const [allWallets, setAllWallets] = useState([])
  const [usersMap, setUsersMap] = useState({}) 
  const [loading, setLoading] = useState(true)
  const [processingId, setProcessingId] = useState(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      // A. AMBIL DATA PENDING TOPUP
      const qTopup = query(
        collectionGroup(db, 'transactions'),
        where('type', '==', 'topup'),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      )
      const snapTopup = await getDocs(qTopup)
      const listTopup = snapTopup.docs.map(doc => ({
        id: doc.id,
        path: doc.ref.path,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }))
      setPendingTopups(listTopup)

      // B. [BARU] AMBIL DATA PENDING WITHDRAW (TARIK SALDO)
      const qWithdraw = query(
        collectionGroup(db, 'transactions'),
        where('type', '==', 'withdraw'),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      )
      const snapWithdraw = await getDocs(qWithdraw)
      const listWithdraw = snapWithdraw.docs.map(doc => ({
        id: doc.id,
        path: doc.ref.path,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }))
      setPendingWithdraws(listWithdraw)

      // C. AMBIL SEMUA SALDO USER (WALLETS)
      const qWallets = query(collection(db, 'wallets'))
      const snapshotWallets = await getDocs(qWallets)
      const listWallets = snapshotWallets.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      }))
      setAllWallets(listWallets)

      // D. AMBIL DATA PROFIL USER (UNTUK NAMA)
      const qUsers = query(collection(db, 'users'))
      const snapshotUsers = await getDocs(qUsers)
      
      const userMapping = {}
      snapshotUsers.forEach((doc) => {
        const data = doc.data()
        userMapping[doc.id] = data.name || data.username || data.email || 'Tanpa Nama'
      })
      setUsersMap(userMapping)

    } catch (error) {
      console.error("Error fetching data:", error)
      alert("Gagal mengambil data. Cek console untuk detail.")
    }
    setLoading(false)
  }

  // ==============================================
  // 1. LOGIKA TOP UP (TERIMA & TOLAK)
  // ==============================================
  const handleApproveTopup = async (tx) => {
    if (!window.confirm(`Setujui top up sebesar ${formatRupiah(tx.totalTransfer)}?`)) return

    setProcessingId(tx.id)
    try {
      const pathSegments = tx.path.split('/')
      const userUid = pathSegments[1]
      
      const walletRef = doc(db, 'wallets', userUid)
      const txRef = doc(db, tx.path)

      await runTransaction(db, async (transaction) => {
        const walletDoc = await transaction.get(walletRef)
        if (!walletDoc.exists()) throw new Error("Wallet tidak ditemukan!")

        const currentBalance = walletDoc.data().balance || 0
        const newBalance = currentBalance + tx.amount

        transaction.update(walletRef, { 
          balance: newBalance,
          updatedAt: new Date()
        })

        transaction.update(txRef, { 
          status: 'success',
          approvedAt: new Date()
        })
      })

      alert("Berhasil! Saldo user telah bertambah.")
      fetchData()

    } catch (error) {
      console.error("Error approving:", error)
      alert("Gagal: " + error.message)
    }
    setProcessingId(null)
  }

  const handleRejectTopup = async (tx) => {
    if (!window.confirm("Yakin ingin menolak transaksi ini?")) return
    
    setProcessingId(tx.id)
    try {
      const txRef = doc(db, tx.path)
      await runTransaction(db, async (transaction) => {
        transaction.update(txRef, { 
            status: 'failed',
            rejectedAt: new Date()
        })
      })
      
      alert("Transaksi ditolak.")
      fetchData()
    } catch (error) {
      console.error("Error rejecting:", error)
      alert("Gagal menolak: " + error.message)
    }
    setProcessingId(null)
  }

  // ==============================================
  // 2. LOGIKA WITHDRAW (TERIMA & TOLAK) - [BARU]
  // ==============================================
  const handleApproveWithdraw = async (tx) => {
    const confirmMsg = `Pastikan Anda SUDAH TRANSFER ke:\n\nBank: ${tx.bankName}\nNo: ${tx.bankNumber}\nA.n: ${tx.accountName}\nNominal: ${formatRupiah(tx.amount)}\n\nLanjutkan setujui dan kurangi saldo user?`;
    
    if (!window.confirm(confirmMsg)) return

    setProcessingId(tx.id)
    try {
      const pathSegments = tx.path.split('/')
      const userUid = pathSegments[1]
      
      const walletRef = doc(db, 'wallets', userUid)
      const txRef = doc(db, tx.path)

      await runTransaction(db, async (transaction) => {
        const walletDoc = await transaction.get(walletRef)
        if (!walletDoc.exists()) throw new Error("Wallet tidak ditemukan!")

        const currentBalance = walletDoc.data().balance || 0
        
        // Cek Saldo Cukup
        if (currentBalance < tx.amount) {
            throw new Error("Saldo user TIDAK CUKUP untuk penarikan ini!")
        }

        // Kurangi Saldo
        const newBalance = currentBalance - tx.amount

        transaction.update(walletRef, { 
          balance: newBalance,
          updatedAt: new Date()
        })

        transaction.update(txRef, { 
          status: 'success',
          approvedAt: new Date()
        })
      })

      alert("Berhasil! Penarikan disetujui & saldo user dikurangi.")
      fetchData()

    } catch (error) {
      console.error("Error approving withdraw:", error)
      alert("Gagal: " + error.message)
    }
    setProcessingId(null)
  }

  const handleRejectWithdraw = async (tx) => {
    if (!window.confirm("Tolak permintaan penarikan ini? (Saldo user tidak akan terpotong)")) return
    
    setProcessingId(tx.id)
    try {
      const txRef = doc(db, tx.path)
      await runTransaction(db, async (transaction) => {
        transaction.update(txRef, { 
            status: 'failed',
            rejectedAt: new Date()
        })
      })
      
      alert("Permintaan penarikan ditolak.")
      fetchData()
    } catch (error) {
      console.error("Error rejecting withdraw:", error)
      alert("Gagal menolak: " + error.message)
    }
    setProcessingId(null)
  }

  return (
    <div className="space-y-8">
      
      {/* BAGIAN 1: PERMINTAAN TOP UP */}
      <div className="bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-700">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          📥 Permintaan Top Up Masuk
          {pendingTopups.length > 0 && (
            <span className="bg-blue-900 text-blue-200 text-xs px-2 py-1 rounded-full border border-blue-700">
              {pendingTopups.length}
            </span>
          )}
        </h2>

        {loading ? (
          <p className="text-gray-400">Loading data...</p>
        ) : pendingTopups.length === 0 ? (
          <div className="text-center py-6 bg-gray-900 rounded-lg text-gray-500 border border-gray-800 text-sm">
            Tidak ada permintaan top up baru.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-700 border-b border-gray-600 text-sm text-gray-300 uppercase tracking-wider">
                  <th className="p-3 rounded-tl-lg">Waktu</th>
                  <th className="p-3">User</th>
                  <th className="p-3">Metode</th>
                  <th className="p-3">Kode Unik</th>
                  <th className="p-3">Total Transfer</th>
                  <th className="p-3 text-right rounded-tr-lg">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {pendingTopups.map((tx) => {
                   const uid = tx.path.split('/')[1];
                   const userName = usersMap[uid] || 'Loading...';
                   return (
                    <tr key={tx.id} className="border-b border-gray-700 hover:bg-gray-700 transition-colors">
                      <td className="py-3 text-sm text-gray-300">{tx.createdAt ? tx.createdAt.toLocaleString() : '-'}</td>
                      <td className="py-3 text-sm font-medium text-white">
                        {userName}
                        <div className="text-xs text-gray-500">{uid.substring(0,8)}...</div>
                      </td>
                      <td className="py-3 text-sm text-gray-300">{tx.method}</td>
                      <td className="py-3 text-sm font-bold text-orange-400">{tx.uniqueCode}</td>
                      <td className="py-3 text-sm font-bold text-green-400">{formatRupiah(tx.totalTransfer)}</td>
                      <td className="py-3 flex justify-end gap-2">
                        <button onClick={() => handleRejectTopup(tx)} disabled={processingId === tx.id} className="px-3 py-1 text-xs text-red-400 hover:bg-red-900/30 rounded border border-red-800 transition">Tolak</button>
                        <button onClick={() => handleApproveTopup(tx)} disabled={processingId === tx.id} className="px-4 py-1 text-xs bg-green-700 text-white hover:bg-green-600 rounded shadow-sm transition">{processingId === tx.id ? '...' : 'Terima'}</button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* BAGIAN 2: PERMINTAAN TARIK SALDO [BARU] */}
      <div className="bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-700">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          📤 Permintaan Tarik Saldo (Withdraw)
          {pendingWithdraws.length > 0 && (
            <span className="bg-orange-900 text-orange-200 text-xs px-2 py-1 rounded-full border border-orange-700">
              {pendingWithdraws.length}
            </span>
          )}
        </h2>

        {loading ? (
          <p className="text-gray-400">Loading data...</p>
        ) : pendingWithdraws.length === 0 ? (
          <div className="text-center py-6 bg-gray-900 rounded-lg text-gray-500 border border-gray-800 text-sm">
            Tidak ada permintaan penarikan saldo.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-700 border-b border-gray-600 text-sm text-gray-300 uppercase tracking-wider">
                  <th className="p-3 rounded-tl-lg">Waktu</th>
                  <th className="p-3">User</th>
                  <th className="p-3">Info Rekening Tujuan</th>
                  <th className="p-3">Nominal Tarik</th>
                  <th className="p-3 text-right rounded-tr-lg">Aksi (Admin)</th>
                </tr>
              </thead>
              <tbody>
                {pendingWithdraws.map((tx) => {
                   const uid = tx.path.split('/')[1];
                   const userName = usersMap[uid] || 'Loading...';
                   return (
                    <tr key={tx.id} className="border-b border-gray-700 hover:bg-gray-700 transition-colors">
                      <td className="py-3 text-sm text-gray-300 align-top">{tx.createdAt ? tx.createdAt.toLocaleString() : '-'}</td>
                      <td className="py-3 text-sm font-medium text-white align-top">
                        {userName}
                        <div className="text-xs text-gray-500">{uid.substring(0,8)}...</div>
                      </td>
                      <td className="py-3 text-sm text-gray-300 align-top">
                        <div className="font-bold text-white">{tx.bankName}</div>
                        <div>No: {tx.bankNumber}</div>
                        <div className="text-gray-400">A.n: {tx.accountName}</div>
                      </td>
                      <td className="py-3 text-sm font-bold text-red-400 align-top">
                        {formatRupiah(tx.amount)}
                      </td>
                      <td className="py-3 flex flex-col items-end gap-2 align-top">
                        <button onClick={() => handleApproveWithdraw(tx)} disabled={processingId === tx.id} className="w-full px-4 py-1 text-xs bg-blue-700 text-white hover:bg-blue-600 rounded shadow-sm transition">{processingId === tx.id ? 'Proses...' : 'Sudah Transfer & Setujui'}</button>
                        <button onClick={() => handleRejectWithdraw(tx)} disabled={processingId === tx.id} className="w-full px-3 py-1 text-xs text-gray-400 hover:bg-gray-700 rounded border border-gray-600 transition">Tolak</button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* BAGIAN 3: DAFTAR SALDO USER */}
      <div className="bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-700">
        <h2 className="text-xl font-bold text-white mb-4">👥 Daftar Saldo User</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-700 text-gray-300 text-sm uppercase tracking-wider">
                <th className="p-3 rounded-tl-lg">Nama User</th> 
                <th className="p-3">Saldo Saat Ini</th>
                <th className="p-3 rounded-tr-lg">Terakhir Update</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {allWallets.map((wallet) => (
                <tr key={wallet.uid} className="hover:bg-gray-700 transition-colors">
                  <td className="p-3">
                    <div className="font-bold text-white text-sm">
                        {usersMap[wallet.uid] || 'User Tidak Dikenal'}
                    </div>
                    <div className="text-xs text-gray-500 font-mono">
                        {wallet.uid}
                    </div>
                  </td>
                  <td className="p-3 font-bold text-white">
                    {formatRupiah(wallet.balance || 0)}
                  </td>
                  <td className="p-3 text-sm text-gray-400">
                    {wallet.updatedAt?.toDate 
                      ? wallet.updatedAt.toDate().toLocaleString() 
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  )
}